import React from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, BookOpen, Users, Award, CheckCircle2 } from 'lucide-react';

const TrainingLicensingPage = () => {
  const trainingTopics = [
    "Life insurance fundamentals and product types",
    "Policy comparison and recommendation strategies",
    "Client advisory and needs analysis skills",
    "Compliance and regulatory education",
    "Sales presentation techniques",
    "Building referral networks",
    "Advanced products (Premium Financing, IUL)",
    "Business building strategies"
  ];

  return (
    <div className="bg-[#020617] min-h-screen pt-20" data-testid="training-licensing-page">
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <Link to="/for-advisors" className="text-[#C5A059] text-sm uppercase tracking-widest mb-4 inline-block hover:text-[#D4AF37]">
              ← Back to For Advisors
            </Link>
            <h1 className="font-serif text-4xl md:text-5xl text-white leading-tight mb-6">
              Training & Licensing
            </h1>
            <p className="text-slate-300 text-xl">
              Our training platform provides step-by-step support for building a successful insurance practice.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <GraduationCap className="w-8 h-8 text-[#C5A059]" />
                <h2 className="font-serif text-3xl text-white">Licensing Support</h2>
              </div>
              <p className="text-slate-400 mb-6">
                If you're not currently licensed, we provide comprehensive support to help you obtain your state life insurance license.
              </p>
              <ul className="space-y-3">
                {[
                  "Pre-licensing study materials and courses",
                  "Exam preparation guidance",
                  "State application process assistance",
                  "Ongoing CE (Continuing Education) support"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-[#C5A059]" />
                    <span className="text-slate-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="flex items-center space-x-3 mb-6">
                <BookOpen className="w-8 h-8 text-[#C5A059]" />
                <h2 className="font-serif text-3xl text-white">Training Program</h2>
              </div>
              <p className="text-slate-400 mb-6">
                Our comprehensive training covers everything you need to succeed in the insurance industry.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {trainingTopics.map((topic, idx) => (
                  <div key={idx} className="flex items-start space-x-2">
                    <CheckCircle2 className="w-4 h-4 text-[#C5A059] mt-0.5 flex-shrink-0" />
                    <span className="text-slate-300 text-sm">{topic}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="bg-slate-900/50 border border-slate-800 p-8 md:p-12">
            <div className="flex items-center space-x-4 mb-6">
              <Users className="w-10 h-10 text-[#C5A059]" />
              <h2 className="font-serif text-2xl text-white">Mentorship Program</h2>
            </div>
            <p className="text-slate-400 mb-6 max-w-3xl">
              Every new advisor is paired with an experienced mentor who provides hands-on guidance during your first months in the business. Your mentor helps you with:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { title: "Client Meetings", desc: "Join experienced advisors on actual client appointments" },
                { title: "Case Design", desc: "Learn how to analyze needs and recommend solutions" },
                { title: "Business Building", desc: "Strategies for finding and developing clients" }
              ].map((item, idx) => (
                <div key={idx} className="bg-slate-800/50 p-6 rounded-lg">
                  <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                  <p className="text-slate-400 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-3xl text-white mb-6">Start Your Training Journey</h2>
          <p className="text-slate-400 mb-8 max-w-2xl mx-auto">
            Apply today and begin your path to becoming a licensed insurance advisor.
          </p>
          <Link
            to="/become-an-agent"
            className="inline-flex items-center justify-center bg-[#C5A059] text-[#020617] hover:bg-[#D4AF37] font-semibold px-8 py-4 uppercase tracking-wider text-sm transition-all"
          >
            Apply Now
          </Link>
        </div>
      </section>
    </div>
  );
};

export default TrainingLicensingPage;
